import { useEffect, useMemo, useState } from "react";
import InvoiceItem from "../components/Invoice/InvoiceItem";
import axios from "axios";
import { invoices } from "../endpoints";
import { Link } from "react-router-dom";
import arrows from "../assets/icons/filter/arrow-down-up-svgrepo-com.svg";
import arrowup from "../assets/icons/filter/arrow-up-svgrepo-com.svg";
import arrowdown from "../assets/icons/filter/arrow-down-svgrepo-com.svg";

interface period {
  date: string;
}

interface data {
  customer: string;
  vendor: string;
  product: string;
  bill_from: string;
  bill_to: string;
  term: string;
  amount: string;
  buy_total: string;
  sell_total: string;
  buy_per: string;
  sell_per: string;
  margin: string;
  software: string;
}

type SortDirection = "asc" | "desc" | "none";

const InvoiceLayout = ({ date }: period) => {
  const [data, setData] = useState<data[]>([]);
  const [loading, setLoading] = useState(true);
  const apiKey = process.env.REACT_APP_API_KEY;
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const [activeFilter, setActiveFilter] = useState("All");
  const [companySort, setCompanySort] = useState<SortDirection>("none");
  const [productSort, setProductSort] = useState<SortDirection>("none");

  useEffect(() => {
    const fetchData = () => {
      if (date !== "") {
        axios
          .post(baseUrl + invoices, null, {
            params: { period: date },
            headers: {
              Authorization: apiKey,
            },
          })
          .then((response) => {
            setData(response.data.results);
            setActiveFilter("All");
            setCompanySort("none");
            setProductSort("none");
          })
          .catch((error) => {
            console.error("There was an error!", error.response);
          })
          .finally(() => setLoading(false));
      }
    };

    fetchData();

    const intervalId = setInterval(fetchData, 600000);

    return () => clearInterval(intervalId);
  }, [apiKey, baseUrl, date]);

  const filteredData = useMemo(() => {
    let result = [...data];

    if (activeFilter !== "All") {
      result = result.filter((item) => item.software === activeFilter);
    }

    if (companySort !== "none") {
      result.sort((a, b) => {
        const aCustomer = a.customer.toLowerCase();
        const bCustomer = b.customer.toLowerCase();
        return companySort === "asc"
          ? aCustomer.localeCompare(bCustomer)
          : bCustomer.localeCompare(aCustomer);
      });
    }

    if (productSort !== "none") {
      result.sort((a, b) => {
        const aProduct = a.product.toLowerCase();
        const bProduct = b.product.toLowerCase();
        return productSort === "asc"
          ? aProduct.localeCompare(bProduct)
          : bProduct.localeCompare(aProduct);
      });
    }

    return result;
  }, [data, activeFilter, companySort, productSort]);

  const handleFilterChange = (filterValue: string) => {
    setActiveFilter(filterValue);
  };

  const handleCompanySort = () => {
    setCompanySort((prev) => {
      if (prev === "none") return "asc";
      if (prev === "asc") return "desc";
      return "none";
    });
  };

  const handleProductSort = () => {
    setProductSort((prev) => {
      if (prev === "none") return "asc";
      if (prev === "asc") return "desc";
      return "none";
    });
  };

  const getButtonClass = (filterValue: string) => {
    const baseClass = "p-2 border mx-1 rounded-xl transition-all duration-300";
    const activeClass =
      "bg-gradient-to-r from-[#4BBFED] to-[#008FC7] text-white";
    const inactiveClass =
      "border-gray-400 hover:bg-gradient-to-r hover:from-[#4BBFED] hover:to-[#008FC7] hover:text-white";

    return `${baseClass} ${
      activeFilter === filterValue ? activeClass : inactiveClass
    }`;
  };

  return (
    <>
      {/* Header Text */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
            <h1 className="font-bold text-3xl sm:text-4xl">
              {loading ? (
                <span className="text-gray-400">Facturen laden...</span>
              ) : (
                `Facturen: ${date}`
              )}
            </h1>
          </div>
          <p className="mt-4">
            {loading ? (
              <span className="text-gray-400">
                Een moment geduld, de gegevens worden geladen.
              </span>
            ) : (
              "Hier een overzicht van alle Facturen"
            )}
          </p>
        </div>
        <Link to={`/invoices/`}>
          <button className="px-4 py-2 bg-gradient-to-r from-[#4BBFED] to-[#008FC7] text-white rounded-lg text-sm sm:text-base transition-all duration-300 ease-in-out hover:scale-105">
            Terug naar Facturen
          </button>
        </Link>
      </div>

      {/* Data */}
      <div className="bg-white m-auto w-full rounded-lg flex flex-col justify-center items-center mt-5">
        <div className="w-full m-2 ml-5 flex justify-between">
          <div>
            <button
              className={getButtonClass("All")}
              onClick={() => handleFilterChange("All")}
            >
              All
            </button>
            <button
              className={getButtonClass("Pax8")}
              onClick={() => handleFilterChange("Pax8")}
            >
              Pax8
            </button>
            <button
              className={getButtonClass("Arrow")}
              onClick={() => handleFilterChange("Arrow")}
            >
              Arrow
            </button>
            {/* <button
              className={getButtonClass("Timechimp")}
              onClick={() => handleFilterChange("Timechimp")}
            >
              Timechimp
            </button>
            <button
              className={getButtonClass("Irma")}
              onClick={() => handleFilterChange("Irma")}
            >
              Irma
            </button> */}
          </div>
          {/* <button className="mx-4 px-4 py-2 bg-gradient-to-r from-[#4BBFED] to-[#008FC7] text-white rounded-xl text-sm sm:text-base transition-all duration-300 ease-in-out hover:scale-105">
            Maak Excel bestand
          </button> */}
        </div>
        <div className="h-full overflow-auto w-[80vw]">
          {/* Table headers */}
          <div className="w-[190vw] bg-[#f1f4f9] rounded-lg text-black flex text-lg m-3 mb-0 p-3">
            <div
              className="ml-[2.5vw] w-[20vw] text-left my-auto cursor-pointer flex items-center select-none"
              onClick={handleCompanySort}
            >
              <p>Klant</p>
              {companySort === "none" && (
                <img src={arrows} alt="sort" className="h-4 w-4 ml-2" />
              )}
              {companySort === "asc" && (
                <img src={arrowup} alt="ascending" className="h-4 w-4 ml-2" />
              )}
              {companySort === "desc" && (
                <img
                  src={arrowdown}
                  alt="descending"
                  className="h-4 w-4 ml-2"
                />
              )}
            </div>
            <p className="w-[15vw] text-center my-auto">Uitgever</p>
            <div
              className="w-[55vw] text-left my-auto cursor-pointer flex items-center select-none"
              onClick={handleProductSort}
            >
              <p>Product</p>
              {productSort === "none" && (
                <img src={arrows} alt="sort" className="h-4 w-4 ml-2" />
              )}
              {productSort === "asc" && (
                <img src={arrowup} alt="ascending" className="h-4 w-4 ml-2" />
              )}
              {productSort === "desc" && (
                <img
                  src={arrowdown}
                  alt="descending"
                  className="h-4 w-4 ml-2"
                />
              )}
            </div>

            <p className="w-[15vw] text-center my-auto">Factuur van</p>
            <p className="w-[15vw] text-center my-auto">Factuur aan</p>
            <p className="w-[15vw] text-center my-auto">Facturatiecyclus</p>
            <p className="w-[5vw] text-center my-auto">Aantal</p>
            <p className="w-[10vw] text-center my-auto">Inkoop totaal</p>
            <p className="w-[10vw] text-center my-auto">Verkoop totaal</p>
            <p className="w-[10vw] text-center my-auto">Inkoop PS</p>
            <p className="w-[10vw] text-center my-auto">Verkoop PS</p>
            <p className="w-[10vw] text-center my-auto">Margin</p>
          </div>

          {/* Display data */}
          <div>
            {loading ? (
              <span className="text-gray-400 ml-3 mt-3">
                Een moment geduld, de gegevens worden geladen.
              </span>
            ) : (
              filteredData.map((invoice, index) => (
                <InvoiceItem
                  key={index}
                  customer={invoice.customer}
                  vendor={invoice.vendor}
                  product={invoice.product}
                  bill_from={invoice.bill_from}
                  bill_to={invoice.bill_to}
                  term={invoice.term}
                  amount={invoice.amount}
                  buy_total={invoice.buy_total}
                  sell_total={invoice.sell_total}
                  buy_per={invoice.buy_per}
                  sell_per={invoice.sell_per}
                  margin={invoice.margin}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceLayout;
